import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import { ClerkProvider, useAuth, SignIn } from '@clerk/react';
import App from './App';
import QuotePage from './QuotePage';
import JobSheetPage from './JobSheetView';
import TermsPage from './TermsPage';


// Clerk's light theme (the default — no baseTheme import needed) rather than
// hand-rolling a dark palette. The dark version needed colorInputBackground/
// colorInputText/formFieldInput/otpCodeFieldInput patched in by hand after the
// OTP screen shipped with unreadable text, and still risked the same class of
// bug anywhere else Clerk renders a field we hadn't checked. Light theme is
// Clerk's own fully-tested set of contrast pairings, so only brand-level
// customisation (colour, font, logo) needs setting here — no colour-scheme
// overrides to keep in sync by hand.
const clerkAppearance = {
  // @clerk/react 6 ("Core 3") renamed the appearance prop's layout key to
  // options - logoPlacement/logoImageUrl/logoLinkUrl living under the old
  // `layout` key were silently ignored (no error, just no <img>), which is
  // why the sign-in screen never showed a logo.
  options: {
    logoPlacement: 'inside',
    // logo-dark.png (dark navy lockup) — not logo.png, which is the white
    // variant used for dark backgrounds (the app header, dark HTML emails) and
    // would be invisible against Clerk's light theme. logo-dark.png is the one
    // already used by the PDF template, which is a white/light document.
    logoImageUrl: 'https://sublime-quotes-git-main-sublime2.vercel.app/logo-dark.png',
    logoLinkUrl: 'https://app.sublimeprecision.co.uk',
  },
  variables: {
    colorPrimary: '#3a52cc',
    borderRadius: '8px',
    fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
    fontFamilyButtons: "'DM Sans', 'Helvetica Neue', sans-serif",
    fontSize: '14px',
  },
  elements: {
    formButtonPrimary: { backgroundColor: '#3a52cc', fontWeight: '700', letterSpacing: '0.05em' },
    footerActionLink: { color: '#3a52cc' },
  },
};

function QuoteRoute() {
  // Public, client-facing — the "View Full Quote" link in emailed quotes
  // lands here. Must never require a Clerk sign-in.
  // Signed-in CRM users also get a way back to the Quote Log; clients (never
  // signed in) see the page exactly as before.
  const { quoteId } = useParams();
  const { isSignedIn } = useAuth();
  return <QuotePage quoteId={quoteId} crmUser={!!isSignedIn} />;
}

// Internal job sheet (hours, crew, costs): signed-in users only.
function JobSheetRoute() {
  const { quoteId } = useParams();
  return <JobSheetPage quoteId={quoteId} />;
}

function RequireAuth({ children }) {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return <div style={{minHeight:"100vh",background:"#0e1520",display:"flex",alignItems:"center",justifyContent:"center",color:"#7a8fa8",fontFamily:"'DM Sans',sans-serif"}}>Loading...</div>;
  if (!isSignedIn) return <div style={{minHeight:"100vh",background:"#0e1520",display:"flex",alignItems:"center",justifyContent:"center"}}><SignIn appearance={clerkAppearance}/></div>;
  return children;
}

function ProtectedApp() {
  return (
    <Routes>
      <Route path="/quote/:quoteId" element={<QuoteRoute />} />
      {/* Public: the Terms of Service clients agree to when accepting online. */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/jobsheet/:quoteId" element={<RequireAuth><JobSheetRoute /></RequireAuth>} />
      <Route path="/*" element={<RequireAuth><App /></RequireAuth>} />
    </Routes>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    {/* afterSignOutUrl lives here now, not on UserButton - Core 3 removed
        that prop from UserButton entirely (see @clerk/upgrade's codemod,
        which already stripped it from App.jsx's <UserButton>). Without it
        set somewhere, sign-out falls back to Clerk's own default redirect
        instead of this app's "/". */}
    <ClerkProvider publishableKey={process.env.REACT_APP_CLERK_PUBLISHABLE_KEY} appearance={clerkAppearance} afterSignOutUrl="/">
      <BrowserRouter>
        <ProtectedApp />
      </BrowserRouter>
    </ClerkProvider>
  </React.StrictMode>
);
